import { useQuery } from '@tanstack/react-query';
import {
  BriefcaseIcon,
  BuildingIcon,
  CalendarIcon,
  CheckCircle2Icon,
  ExternalLinkIcon,
  MailIcon,
  PhoneIcon,
  TrendingUpIcon,
  UserIcon,
  WalletIcon,
  XCircleIcon,
} from 'lucide-react';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/avatar/avatar';
import { Badge } from '@/components/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Skeleton } from '@/components/skeleton/skeleton';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import {
  getUserBalanceQueryOptions,
  type UserBalance,
} from '@/features/users/api/get-user-balance.api';
import { getUserQueryOptions } from '@/features/users/api/get-user.api';
import type { User } from '@/features/users/types/user.type';
import {
  getMentorDisciplineStatusLabel,
  getMentorDisciplineStatusVariant,
} from '@/features/users/utils/mentor-discipline-status';

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

function formatRupiah(amount: number | null | undefined) {
  if (amount == null) return '-';
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

function getInitials(name: string | null | undefined) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function genderLabel(gender: string | null | undefined) {
  if (gender === 'male') return 'Laki-laki';
  if (gender === 'female') return 'Perempuan';
  return '-';
}

export interface DetailUserDialogProps extends BaseDialogProps {
  userId: string;
}

export default function DetailUserDialog({
  dialog,
  userId,
}: DetailUserDialogProps) {
  const { closeDetailUserDialog } = useV2DialogContext();

  const getUserQuery = useQuery(getUserQueryOptions({ user_id: userId }));

  const user = getUserQuery.data?.result;
  const isMentor = user?.type === 'MENTOR';

  const getUserBalanceQuery = useQuery({
    ...getUserBalanceQueryOptions({ user_id: userId }),
    enabled: isMentor,
  });

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDetailUserDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Detail Pengguna</DialogTitle>
          <DialogDescription>
            Informasi lengkap tentang pengguna ini.
          </DialogDescription>
        </DialogHeader>

        {getUserQuery.isPending ? (
          <DetailUserSkeleton />
        ) : getUserQuery.isError || !user ? (
          <p className="text-sm text-destructive">
            Gagal memuat data pengguna. Silakan coba lagi.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            <DetailUserHeader user={user} />
            {isMentor && (
              <DetailUserBalance
                isLoading={getUserBalanceQuery.isPending}
                isError={getUserBalanceQuery.isError}
                balance={getUserBalanceQuery.data?.result}
              />
            )}
            <DetailUserInfoGrid user={user} />
            {user.about && <DetailUserAbout about={user.about} />}
            <DetailUserSocial user={user} />
            <DetailUserMeta user={user} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DetailUserHeaderProps {
  user: User;
}

function DetailUserHeader({ user }: DetailUserHeaderProps) {
  return (
    <div className="flex items-start gap-4">
      <Avatar size="lg" className="size-16">
        {user.image_profile && (
          <AvatarImage src={user.image_profile} alt={user.full_name} />
        )}
        <AvatarFallback className="text-base">
          {getInitials(user.full_name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-1 flex-col gap-1.5">
        <p className="text-base font-semibold text-foreground">
          {user.full_name}
        </p>
        <p className="text-xs text-muted-foreground">{user.email}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Badge variant={user.status === 'active' ? 'success' : 'destructive'}>
            {user.status === 'active' ? 'Aktif' : 'Nonaktif'}
          </Badge>
          <Badge variant={user.is_verified ? 'info' : 'neutral'}>
            {user.is_verified ? (
              <CheckCircle2Icon className="size-3" />
            ) : (
              <XCircleIcon className="size-3" />
            )}
            {user.is_verified ? 'Terverifikasi' : 'Belum verifikasi'}
          </Badge>
          {user.type === 'MENTOR' && (
            <Badge
              variant={getMentorDisciplineStatusVariant(
                user.mentor_discipline_status
              )}
            >
              Pembinaan:{' '}
              {getMentorDisciplineStatusLabel(user.mentor_discipline_status)}
            </Badge>
          )}
          {user.type && (
            <Badge
              variant={user.type === 'MENTOR' ? 'mentor' : 'secondary'}
              className="lowercase"
            >
              {user.type}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

interface DetailUserBalanceProps {
  isLoading: boolean;
  isError: boolean;
  balance: UserBalance | undefined;
}

function DetailUserBalance({
  isLoading,
  isError,
  balance,
}: DetailUserBalanceProps) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <WalletIcon className="size-4" />
        <span>Saldo</span>
      </div>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError || !balance ? (
        <p className="text-sm text-destructive">Gagal memuat saldo.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <BalanceStat
            icon={<WalletIcon className="size-4" />}
            label="Saldo Saat Ini"
            value={formatRupiah(balance.balance)}
          />
          <BalanceStat
            icon={<TrendingUpIcon className="size-4" />}
            label="Total Pendapatan"
            value={formatRupiah(balance.total_earned)}
          />
          <BalanceStat
            icon={<TrendingUpIcon className="size-4 rotate-180" />}
            label="Total Penarikan"
            value={formatRupiah(balance.total_withdrawn)}
          />
        </div>
      )}
    </div>
  );
}

interface BalanceStatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function BalanceStat({ icon, label, value }: BalanceStatProps) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-background p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}

interface DetailUserInfoGridProps {
  user: User;
}

function DetailUserInfoGrid({ user }: DetailUserInfoGridProps) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
      <InfoRow
        icon={<UserIcon className="size-4" />}
        label="Jenis Kelamin"
        value={genderLabel(user.gender)}
      />
      <InfoRow
        icon={<PhoneIcon className="size-4" />}
        label="Nomor Telepon"
        value={user.phone_number ?? '-'}
      />
      <InfoRow
        icon={<MailIcon className="size-4" />}
        label="Email"
        value={user.email}
      />
      <InfoRow
        icon={<BuildingIcon className="size-4" />}
        label="Institusi"
        value={user.institution ?? '-'}
      />
      <InfoRow
        icon={<BriefcaseIcon className="size-4" />}
        label="Posisi"
        value={user.position ?? '-'}
      />
    </div>
  );
}

interface DetailUserAboutProps {
  about: string;
}

function DetailUserAbout({ about }: DetailUserAboutProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium text-muted-foreground">Tentang</p>
      <p className="text-sm leading-relaxed text-foreground">{about}</p>
    </div>
  );
}

interface DetailUserSocialProps {
  user: User;
}

function DetailUserSocial({ user }: DetailUserSocialProps) {
  if (!user.linkedin_url && !user.username_instagram) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">Sosial Media</p>
      <div className="flex flex-wrap gap-2">
        {user.linkedin_url && (
          <a
            href={user.linkedin_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-muted"
          >
            <ExternalLinkIcon className="size-3.5" />
            LinkedIn
          </a>
        )}
        {user.username_instagram && (
          <a
            href={`https://instagram.com/${user.username_instagram}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-muted"
          >
            <ExternalLinkIcon className="size-3.5" />
            Instagram @{user.username_instagram}
          </a>
        )}
      </div>
    </div>
  );
}

interface DetailUserMetaProps {
  user: User;
}

function DetailUserMeta({ user }: DetailUserMetaProps) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Dibuat Pada"
        value={formatDate(user.created_at)}
      />
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Diperbarui Pada"
        value={formatDate(user.updated_at)}
      />
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="wrap-break-word text-sm font-medium text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}

function DetailUserSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-4">
        <Skeleton className="size-16 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-56" />
          <div className="mt-1 flex gap-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
        </div>
      </div>
      <Skeleton className="h-32 w-full rounded-lg" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}
